import { useState, useEffect } from 'react';
import { apiClient } from '../../api_actions';
import { orbitalSpinner } from '../LoadingOrbitalSpinner/LoadingOrbitalSpinner';
import { chartTheme } from '../../constants/chartTheme';

// Separate data transformation
const transformUserStatsForChart = (users) => {
    const topUsers = users
        .map(user => {
            const totalEndpoints = Object.values(user.portal_log_summary.endpoint_hits).reduce((sum, val) => sum + val, 0);
            return {
                ...user,
                totalEndpoints
            };
        })
        .sort((a, b) => b.totalEndpoints - a.totalEndpoints);

    const endpointTotals = {};
    topUsers.forEach(user => {
        Object.entries(user.portal_log_summary.endpoint_hits).forEach(([endpoint, count]) => {
            endpointTotals[endpoint] = (endpointTotals[endpoint] || 0) + count;
        });
    });

    // Sort endpoints by total hits descending
    const sortedEndpoints = Object.keys(endpointTotals).sort((a, b) => endpointTotals[b] - endpointTotals[a]);

    // Transform data into chart format with endpoint counts
    const transformedData = topUsers.map(user => {
        const dataPoint = {
            name: `${user.first_name} ${user.last_name}`
        };
        sortedEndpoints.forEach(endpoint => {
            dataPoint[endpoint] = user.portal_log_summary.endpoint_hits[endpoint] || 0;
        });
        return dataPoint;
    });

    return { data: transformedData, endpoints: sortedEndpoints };
};

// Separate API call
const fetchEndpointUsageData = async (startAndEndUnixDatesParams = '') => {
    try {
        const res = await apiClient.post(`read/portallogs?order=balanced&${startAndEndUnixDatesParams()}`);
        if (res && res.data) {
            return transformUserStatsForChart(res.data);
        }
        return { data: [], endpoints: [] };
    } catch (error) {
        console.error('Failed to load data:', error);
        return { data: [], endpoints: [] };
    }
};

// Factory function - simplified
const createEndpointUsageConfig = (series = [], data = []) => ({
    theme: chartTheme,
    title: { text: 'Website Use by User'},
    subtitle: { text: 'Top and bottom 5 users by endpoint hits' },
    series: series,
    data: data,
    overlays: {
        loading: {
            renderer: orbitalSpinner,
        },
    },
    // ADD THIS AXES CONFIGURATION:
    axes: {
        x: {
            type: 'category',
            label: { 
                fontSize: 12,
                rotation: 45,        // Fixed rotation, not auto
                minSpacing: 0,       // Force all labels
             },
        },
    }
});

export const EndpointUsageChartLogic = (startAndEndUnixDatesParams = '') => {
    const [endpointUsageConfig, setEndpointUsageConfig] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const result = await fetchEndpointUsageData(startAndEndUnixDatesParams);

            const series = result.endpoints.map(endpoint => ({
                type: 'bar',
                xKey: 'name',
                yKey: endpoint,
                yName: endpoint,
                stacked: true
            }));

            setEndpointUsageConfig(createEndpointUsageConfig(series, result.data));
            setLoading(false);
        };

        loadData();
    }, [startAndEndUnixDatesParams]);

    return {
        chartConfig: endpointUsageConfig,
        loading,
    };
};
